import { useEffect, useRef, useState } from "react";
import { useShallow } from "zustand/shallow";
import { Button, Chip, Listbox, ListboxItem } from "@heroui/react";
import { CheckIcon, CircleIcon, MagnifyingGlassIcon, XIcon } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import clsx from "clsx";

import { FetchedTag } from "@/services/api/fetchers/tags";
import { ICON_SIZE } from "@/config/constants";
import { useBoundStore } from "@/app/store";
import { storage } from "@/shared/utils/local-storage";
import { getTagBgColorClassName, getTagColorClassName } from "@/features/tags/utils";

interface NotesSearchProps {
  tags: FetchedTag[];
  selectedTags?: FetchedTag[];
  onSearchChange?: (value: string) => void;
  onTagsChange?: (tags: FetchedTag[]) => void;
}

export function NotesSearchDesktop({
  tags,
  selectedTags: parentSelectedTags = [],
  onSearchChange,
  onTagsChange,
}: NotesSearchProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState("");
  const [selectedTags, setSelectedTags] = useState<FetchedTag[]>(parentSelectedTags);
  const [selectedKey, setSelectedKey] = useState<React.Key>();
  const [isActive, setIsActive] = useState(false);
  const { deviceType } = useBoundStore(useShallow((state) => ({ deviceType: state.device.type })));
  const noTagsToDisplay = deviceType === "desktop" ? 2 : 1;

  // Helper function to check if input contains tag search
  const isTagSearch = (value: string) => value.includes("#");

  // Helper function to extract tag search part
  const getTagSearchTerm = (value: string) => {
    const hashIndex = value.lastIndexOf("#");
    return hashIndex !== -1 ? value.substring(hashIndex + 1) : "";
  };

  const filteredTags = tags.filter((tag) =>
    tag.name.toLowerCase().includes(getTagSearchTerm(searchValue).toLowerCase())
  );

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);
    storage.setNotesSearch({ text: value, tags: selectedTags });

    if (isTagSearch(value)) {
      const tagTerm = getTagSearchTerm(value);
      const filtered = tags
        .filter((tag) => !selectedTags.some((selected) => selected.id === tag.id))
        .filter((tag) => tag.name.toLowerCase().includes(tagTerm.toLowerCase()));

      setSelectedKey(filtered[0]?.id);
    } else {
      onSearchChange?.(value);
      setSelectedKey(undefined);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (isTagSearch(searchValue)) {
      if (event.key === "Enter" && selectedKey) {
        const tagToAdd = filteredTags.find((tag) => tag.id === selectedKey);
        if (tagToAdd) {
          handleTagSelection(tagToAdd);
        }
      } else if (event.key === "Escape") {
        setSearchValue("");
        setSelectedKey(undefined);
        inputRef.current?.blur();
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        setSelectedKey((current) => {
          const index = filteredTags.findIndex((tag) => tag.id === current);
          return filteredTags[index + 1]?.id ?? filteredTags[0]?.id;
        });
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setSelectedKey((current) => {
          const index = filteredTags.findIndex((tag) => tag.id === current);
          return filteredTags[index - 1]?.id ?? filteredTags[filteredTags.length - 1]?.id;
        });
      }
    } else if (event.key === "Escape") {
      inputRef.current?.blur();
    } else if (event.key === "Backspace" && searchValue === "") {
      setSelectedTags(selectedTags.slice(0, -1));
      onTagsChange?.(selectedTags.slice(0, -1));
      storage.setNotesSearch({ text: searchValue, tags: selectedTags.slice(0, -1) });
    } else if (event.key === "Enter") {
      inputRef.current?.blur();
    } else if (event.key === "Delete") {
      handleClearAll();
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      // Check if focus is still within the search component
      if (containerRef.current && !containerRef.current.contains(document.activeElement)) {
        setIsActive(false);
        if (searchValue === "#") {
          setSearchValue("");
        }
      }
    }, 100);
  };

  const handleSearchButtonClick = () => {
    setIsActive(true);
  };

  const handleButtonKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleSearchButtonClick();
    }
  };

  const handleDeleteTag = (tag: FetchedTag) => {
    const newTags = selectedTags.filter((selected) => selected.id !== tag.id);
    setSelectedTags(newTags);
    onTagsChange?.(newTags);
    storage.setNotesSearch({ text: searchValue, tags: newTags });
  };

  const handleClearAll = () => {
    setSelectedTags([]);
    setSearchValue("");
    setSelectedKey(undefined);
    storage.setNotesSearch({ text: "", tags: [] });
    onSearchChange?.("");
    onTagsChange?.([]);
  };

  const handleTagSelection = (tag: FetchedTag) => {
    const isTagSelected = selectedTags.some((selectedTag) => selectedTag.id === tag.id);
    let newTags: FetchedTag[];

    if (isTagSelected) {
      newTags = selectedTags.filter((selectedTag) => selectedTag.id !== tag.id);
    } else {
      newTags = [...selectedTags, tag];
    }

    setSelectedTags(newTags);
    onTagsChange?.(newTags);

    // Clear the search value or remove the tag search part
    const hashIndex = searchValue.lastIndexOf("#");
    const newSearchValue = hashIndex !== -1 ? searchValue.substring(0, hashIndex) : "";
    setSearchValue(newSearchValue);
    storage.setNotesSearch({ text: newSearchValue, tags: newTags });
    setSelectedKey(undefined);
    inputRef.current?.focus();
  };

  // Sync with parent selectedTags
  useEffect(() => {
    setSelectedTags(parentSelectedTags);
  }, [parentSelectedTags]);

  useEffect(() => {
    // Don't restore from localStorage if parent already has tags (URL-driven state)
    if (parentSelectedTags.length > 0) {
      return;
    }

    const storedSearch = storage.getNotesSearch();

    // Only restore if there's something meaningful to restore
    if (storedSearch.text || storedSearch.tags.length > 0) {
      if (storedSearch.text) {
        setSearchValue(storedSearch.text);
        onSearchChange?.(storedSearch.text);
      }

      if (storedSearch.tags.length > 0) {
        setSelectedTags(storedSearch.tags);
        onTagsChange?.(storedSearch.tags);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to run only on mount

  useEffect(() => {
    if (selectedKey && listboxRef.current) {
      const selectedElement = listboxRef.current.querySelector(`[data-key="${selectedKey}"]`);
      selectedElement?.scrollIntoView({ block: "nearest" });
    }
  }, [selectedKey]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "/" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (!isActive) {
          setIsActive(true);
          setTimeout(() => {
            inputRef.current?.focus();
          }, 0);
        } else {
          inputRef.current?.focus();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isActive]);

  return (
    <motion.div
      ref={containerRef}
      className={clsx(
        "transition-duration-150 relative flex min-h-10 items-center gap-2 rounded-xl px-3 py-1 transition-opacity",
        {
          "border-default cursor-text border": isActive,
          "focus:ring-primary cursor-pointer hover:opacity-90 focus:ring-1 focus:ring-offset-1 focus:outline-none":
            !isActive,
        }
      )}
      onClick={!isActive ? handleSearchButtonClick : undefined}
      onKeyDown={!isActive ? handleButtonKeyDown : undefined}
      role={!isActive ? "button" : "group"}
      tabIndex={!isActive ? 0 : -1}
      aria-label={
        !isActive
          ? selectedTags.length || searchValue
            ? `Search notes (${selectedTags.length} tags selected${searchValue ? ", search active" : ""})`
            : "Search notes"
          : "Search form"
      }
      aria-expanded={isActive}
      animate={{
        width: isActive ? "100%" : "min-content",
      }}
      transition={{
        duration: 0.15,
        ease: "easeInOut",
      }}
    >
      <MagnifyingGlassIcon
        weight={(searchValue || selectedTags.length > 0) && !isActive ? "fill" : "regular"}
        className="shrink-0"
        size={ICON_SIZE.md}
        aria-hidden="true"
      />

      {isActive && selectedTags.length ? (
        <div
          id="selected-tags"
          className="scrollbar-hide flex max-w-1/2 min-w-min gap-1 overflow-y-hidden"
          role="group"
          aria-label={`Selected tags: ${selectedTags
            .slice(0, noTagsToDisplay)
            .map((tag) => tag.name)
            .join(
              ", "
            )}${selectedTags.length > noTagsToDisplay ? ` and ${selectedTags.length - noTagsToDisplay} more` : ""}`}
        >
          {selectedTags.slice(0, noTagsToDisplay).map((tag) => (
            <Chip
              aria-label={`Selected tag: ${tag.name}`}
              classNames={{ dot: getTagBgColorClassName(tag.color) }}
              endContent={
                <Button
                  aria-label={`Remove ${tag.name} tag`}
                  radius="full"
                  className="ml-1 h-4 min-h-4 w-4 min-w-4 p-0"
                  isIconOnly
                  variant="light"
                  onPress={() => handleDeleteTag(tag)}
                >
                  <XIcon size={12} aria-hidden="true" />
                </Button>
              }
              radius="sm"
              role="group"
              size="sm"
              variant="dot"
              key={tag.id}
            >
              {tag.name}
            </Chip>
          ))}
          {selectedTags.length > noTagsToDisplay ? (
            <Chip
              radius="sm"
              size="sm"
              aria-label={`${selectedTags.length - noTagsToDisplay} more tags selected`}
              variant="flat"
            >
              +{selectedTags.length - noTagsToDisplay}
            </Chip>
          ) : null}
        </div>
      ) : null}
      {isActive && (
        <input
          autoFocus
          ref={inputRef}
          className="placeholder:text-foreground-500 w-full bg-transparent text-sm outline-none focus:outline-none"
          placeholder={selectedTags.length ? "Search..." : "Search notes or use # for tags"}
          type="text"
          value={searchValue}
          onChange={handleSearch}
          onKeyDown={handleKeyDown}
          onBlur={handleInputBlur}
          aria-label="Search input"
          aria-describedby={selectedTags.length ? "selected-tags" : undefined}
          role="searchbox"
          aria-autocomplete="list"
          aria-expanded={isTagSearch(searchValue)}
        />
      )}

      {isActive && (selectedTags.length || searchValue) ? (
        <Button
          aria-label="Clear all search and tags"
          className="h-5 min-h-5 w-5 min-w-5 p-0"
          isIconOnly
          onPress={handleClearAll}
          radius="full"
          size="sm"
          variant="flat"
        >
          <XIcon size={10} weight="bold" aria-hidden="true" />
        </Button>
      ) : null}

      {isTagSearch(searchValue) ? (
        <div
          className={clsx("border-default bg-content1 absolute left-0 z-50 flex w-full rounded-xl border shadow-2xl", {
            "top-10": deviceType === "desktop",
            "top-11": deviceType === "mobile",
          })}
          role="region"
          aria-label="Tag suggestions"
        >
          <Listbox
            ref={listboxRef}
            className="max-h-52 w-full overflow-y-auto scroll-smooth"
            onSelectionChange={(keys) => setSelectedKey(Array.from(keys)[0])}
            aria-label="Available tags"
            role="listbox"
          >
            {filteredTags.map((tag) => (
              <ListboxItem
                data-key={tag.id}
                className={clsx("transition-colors", {
                  "bg-primary text-primary-foreground hover:bg-primary! hover:text-primary-foreground!":
                    selectedKey === tag.id,
                })}
                endContent={
                  selectedTags.find((selectedTag) => selectedTag.id === tag.id) ? (
                    <CheckIcon size={ICON_SIZE.sm} />
                  ) : null
                }
                key={tag.id}
                startContent={
                  <CircleIcon className={getTagColorClassName(tag.color)} size={ICON_SIZE.md} weight="duotone" />
                }
                onPress={() => handleTagSelection(tag)}
              >
                {tag.name}
              </ListboxItem>
            ))}
          </Listbox>
        </div>
      ) : null}
    </motion.div>
  );
}
